import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import type { ICareHome } from '../../interfaces';
import type { IAccount } from '../../interfaces/Account/IAccount.ts';
import type { ICreateStationPayload } from '../../interfaces/Station/ICreateStationPayload.ts';
import AccountService from '../../services/AccountService.ts';
import CareHomeService from '../../services/CareHomeService.ts';
import StationService from '../../services/StationService.ts';

type CareHomePageKeys = {
  careHomeQueryKey: readonly ['careHome', string | undefined];
  stationsQueryKey: readonly ['stations', string | undefined];
  cockpitUsersQueryKey: readonly ['cockpitUsers', string | undefined];
};

type CareHomePageMutationCallbacks = {
  onLivingAreaDone: () => void;
  onClearEditingStation: () => void;
  onClearStationToDelete: () => void;
  onShowEditInfo: () => void;
  onShowDeleteInfo: () => void;
  onShowDeleteError: () => void;
  onShowResendInfo: () => void;
};

export function useCareHomePageMutations(
  keys: CareHomePageKeys,
  callbacks: CareHomePageMutationCallbacks
) {
  const queryClient = useQueryClient();

  const updateAccountResendLink = (id: string) => {
    queryClient.setQueryData<IAccount[]>(keys.cockpitUsersQueryKey, (prev) => {
      if (!prev) return prev;
      return produce(prev, (draft) => {
        const account = draft.find((account) => account.id === id);
        if (account) {
          account.updated_at = new Date().toISOString();
        }
      });
    });
  };

  const updateCareHomeStatusMutation = useMutation({
    mutationFn: (variables: { careHomeId: string; isActive: boolean }) =>
      CareHomeService.updateCareHomeActiveStatus(
        variables.careHomeId,
        variables.isActive
      ),
    onSuccess: async (updated) => {
      queryClient.setQueryData<ICareHome>(keys.careHomeQueryKey, updated);
      // Keep care-home-list in sync
      await queryClient.invalidateQueries({ queryKey: ['careHomes'] });
    },
  });

  const upsertStationMutation = useMutation({
    mutationFn: (variables: {
      stationId?: string;
      payload: ICreateStationPayload;
      isEdit: boolean;
    }) => {
      if (variables.stationId) {
        return StationService.updateStation(
          variables.stationId,
          variables.payload
        ).then(() => undefined);
      }
      return StationService.createStation(variables.payload).then(() => undefined);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: keys.stationsQueryKey });
      if (variables.isEdit) {
        callbacks.onShowEditInfo();
      }
    },
    onSettled: () => {
      callbacks.onClearEditingStation();
      callbacks.onLivingAreaDone();
    },
  });

  const deleteStationMutation = useMutation({
    mutationFn: (stationId: string) => StationService.deleteStation(stationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.stationsQueryKey });
      callbacks.onShowDeleteInfo();
    },
    onError: (e: any) => {
      const statusCode = e?.response?.data?.status_code ?? e?.response?.status;
      if (statusCode === 400) {
        callbacks.onShowDeleteError();
      }
    },
    onSettled: () => {
      callbacks.onClearStationToDelete();
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: (accountId: string) => AccountService.initiateResetPassword(accountId),
    onSuccess: (_data, accountId) => {
      callbacks.onShowResendInfo();
      updateAccountResendLink(accountId);
    },
  });

  const isCrudLoading =
    updateCareHomeStatusMutation.isPending ||
    upsertStationMutation.isPending ||
    deleteStationMutation.isPending ||
    resendInviteMutation.isPending;

  const setCareHomeCache = (updated: ICareHome) => {
    queryClient.setQueryData(keys.careHomeQueryKey, updated);
  };

  const invalidateCockpitUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.cockpitUsersQueryKey });
  };

  return {
    updateCareHomeStatusMutation,
    upsertStationMutation,
    deleteStationMutation,
    resendInviteMutation,
    isCrudLoading,
    setCareHomeCache,
    invalidateCockpitUsers,
  };
}

